import React from "react";
import {
    FiArrowRight,
    FiBarChart2,
    FiBookOpen,
    FiCheckCircle,
    FiCpu,
    FiLayers,
    FiMail,
    FiPhone,
    FiXCircle,
} from "react-icons/fi";
import { Link } from "react-router-dom";
import "../css/lms.scss";

const featureIcons = {
  cpu: <FiCpu />,
  layers: <FiLayers />,
  chart: <FiBarChart2 />,
};

const fallbackData = {
  heroLogoAlt: "Tomko LMS",
  heroEyebrow: "Tomko Learning Management System",
  heroTitle: "Create and manage digital lessons at modern EdTech speed.",
  heroLead:
    "Tomko LMS helps schools, training centers, and education providers build high-quality digital learning programs faster and at a lower production cost.",
  requestIntro: "Register your interest",
  exploreFeatures: "Explore features",
  heroActionsAriaLabel: "Tomko LMS actions",
  dashboardAriaLabel: "Tomko LMS platform preview",
  dashboardTitle: "Course Studio",
  dashboardStats: [
    { label: "Lessons", value: "128" },
    { label: "Published", value: "84%" },
  ],
  dashboardLessonTitle: "English A1 lesson builder",
  dashboardLessonText: "Vocabulary, listening, speaking practice",
  comparisonEyebrow: "Why Tomko LMS",
  comparisonTitle: "Tomko LMS vs Traditional LMS",
  comparisonHeaders: {
    metric: "Metric",
    traditional: "Other LMS",
    tomko: "Tomko LMS",
  },
  comparisonItems: [],
  includedLabel: "Included",
  notIncludedLabel: "Not included",
  featuresEyebrow: "Platform capabilities",
  featuresTitle: "Built for fast, repeatable lesson production",
  features: [],
  workflowEyebrow: "Content workflow",
  workflowTitle: "From curriculum plan to live lesson",
  workflowText:
    "Tomko LMS supports the full lesson lifecycle, while Gobicraft can integrate the LMS with existing school systems and education operations.",
  workflowItems: [],
  contactEyebrow: "Contact Tomko LMS",
  contactTitle: "Talk with us about LMS setup and integration",
  contactAriaLabel: "Tomko LMS contact details",
  phone: "+976 8070-8476",
  phoneHidden: true,
  email: "contact@gobicraft.com",
};

const ComparisonValue = ({ value, positive = false, data }) => {
  if (typeof value !== "boolean") {
    return <span>{value}</span>;
  }

  return value ? (
    <FiCheckCircle
      className={positive ? "lms-comparison__check" : ""}
      aria-label={data.includedLabel}
    />
  ) : (
    <FiXCircle className="lms-comparison__x" aria-label={data.notIncludedLabel} />
  );
};

const Lms = ({ data: pageData = {} }) => {
  const data = { ...fallbackData, ...pageData };
  const comparisonHeaders = {
    ...fallbackData.comparisonHeaders,
    ...data.comparisonHeaders,
  };
  const showPhone = Boolean(data.phone) && !data.phoneHidden;
  const phoneHref = showPhone ? `tel:${data.phone.replace(/[\s-]/g, "")}` : undefined;

  return (
    <main className="lms-page">
      <section className="lms-hero" aria-labelledby="lms-hero-title">
        <div className="lms-hero__content">
          <img
            className="lms-hero__logo"
            src="/images/tomko-lms-logo.png"
            alt={data.heroLogoAlt}
          />
          <p className="lms-hero__eyebrow">{data.heroEyebrow}</p>
          <h1 id="lms-hero-title">{data.heroTitle}</h1>
          <p className="lms-hero__lead">{data.heroLead}</p>
          <div className="lms-hero__actions" aria-label={data.heroActionsAriaLabel}>
            <Link className="lms-button lms-button--primary" to="/school/client">
              <span>{data.requestIntro}</span>
              <FiArrowRight aria-hidden="true" />
            </Link>
            <a className="lms-button lms-button--secondary" href="#lms-features">
              {data.exploreFeatures}
            </a>
          </div>
        </div>

        <div className="lms-hero__visual" aria-label={data.dashboardAriaLabel}>
          <div className="lms-dashboard">
            <div className="lms-dashboard__topbar">
              <span />
              <strong>{data.dashboardTitle}</strong>
            </div>
            <div className="lms-dashboard__body">
              <aside className="lms-dashboard__sidebar">
                <span className="active" />
                <span />
                <span />
                <span />
              </aside>
              <div className="lms-dashboard__main">
                <div className="lms-dashboard__summary">
                  {data.dashboardStats.map((item) => (
                    <div key={item.label}>
                      <span>{item.label}</span>
                      <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>
                <div className="lms-dashboard__lesson">
                  <FiBookOpen aria-hidden="true" />
                  <div>
                    <strong>{data.dashboardLessonTitle}</strong>
                    <span>{data.dashboardLessonText}</span>
                  </div>
                </div>
                <div className="lms-dashboard__progress">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="lms-section lms-comparison" aria-labelledby="lms-comparison-title">
        <div className="lms-section__header">
          <p className="lms-section__eyebrow">{data.comparisonEyebrow}</p>
          <h2 id="lms-comparison-title">{data.comparisonTitle}</h2>
        </div>
        <div className="lms-comparison__table" role="table">
          <div className="lms-comparison__row lms-comparison__row--header" role="row">
            <div role="columnheader">{comparisonHeaders.metric}</div>
            <div role="columnheader">{comparisonHeaders.traditional}</div>
            <div role="columnheader">{comparisonHeaders.tomko}</div>
          </div>
          {data.comparisonItems.map((item) => (
            <div className="lms-comparison__row" role="row" key={item.metric}>
              <div className="lms-comparison__metric" role="cell">
                {item.metric}
              </div>
              <div className="lms-comparison__other" role="cell" data-label={comparisonHeaders.traditional}>
                <ComparisonValue value={item.traditional} data={data} />
              </div>
              <div className="lms-comparison__tomko" role="cell" data-label={comparisonHeaders.tomko}>
                <ComparisonValue value={item.tomko} positive data={data} />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="lms-section lms-section--features" id="lms-features">
        <div className="lms-section__header">
          <p className="lms-section__eyebrow">{data.featuresEyebrow}</p>
          <h2>{data.featuresTitle}</h2>
        </div>
        <div className="lms-feature-grid">
          {data.features.map((item) => (
            <article className="lms-feature" key={item.title}>
              <span className="lms-feature__icon">
                {featureIcons[item.icon] || featureIcons.cpu}
              </span>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="lms-section lms-workflow">
        <div className="lms-workflow__copy">
          <p className="lms-section__eyebrow">{data.workflowEyebrow}</p>
          <h2>{data.workflowTitle}</h2>
          <p>{data.workflowText}</p>
        </div>
        <ol className="lms-workflow__list">
          {data.workflowItems.map((item, index) => (
            <li key={item}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <p>{item}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="lms-section lms-audience" id="lms-audience">
        <div>
          <p className="lms-section__eyebrow">{data.contactEyebrow}</p>
          <h2>{data.contactTitle}</h2>
        </div>
        <div className="lms-audience__contacts" aria-label={data.contactAriaLabel}>
          {showPhone && (
            <a href={phoneHref}>
              <FiPhone aria-hidden="true" />
              <span>{data.phone}</span>
            </a>
          )}
          <a href={`mailto:${data.email}`}>
            <FiMail aria-hidden="true" />
            <span>{data.email}</span>
          </a>
        </div>
      </section>
    </main>
  );
};

export default Lms;
